import styled from '@emotion/styled'
import React, { useMemo, useState } from 'react'

import { ReactComponent as LandingLogo } from '@/assets/icons/landing.svg'
import { Dropdown } from '@/components'
import { useUserContext } from '@/context'
import { APP_TYPE } from '@/types/User'

import { COLORS } from '../../../tailwind.colors'
import { Logout } from './Logout'

type Props = {
  activeApp: APP_TYPE
  minimize: boolean
  smallScreen: boolean
}

export const UserNavigation = ({ activeApp, minimize, smallScreen }: Props) => {
  const { user } = useUserContext()
  const [userOptionsExpanded, setUserOptionsExpanded] = useState(false)

  const userInitials = useMemo(() => {
    if (user) {
      return user.fullName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
    }

    // return L for Landing as default in case there is an ambiguous name.
    return 'L'
  }, [user])

  return (
    <>
      {user && (
        <Dropdown
          dropdownWrapperStyle={styles.dropdownContainer}
          expanded={userOptionsExpanded}
          onHideDropdown={() => setUserOptionsExpanded(false)}
          renderToggle={() => {
            return (
              <UserStyled activeApp={activeApp} smallScreen={smallScreen}>
                <InitialsContainer smallScreen={smallScreen}>
                  <InitialsStyled activeApp={activeApp}>{userInitials}</InitialsStyled>
                </InitialsContainer>
                {!minimize && (
                  <TitleContainerStyled smallScreen={smallScreen}>
                    <TitleStyled activeApp={activeApp} smallScreen={smallScreen}>
                      {user.email}{' '}
                    </TitleStyled>
                  </TitleContainerStyled>
                )}
              </UserStyled>
            )
          }}
          setExpanded={setUserOptionsExpanded}
        >
          {!smallScreen ? (
            <div className="grid w-full justify-center py-2 md:py-4">
              <LandingLogo className="w-36 icon-semantic-black" />
            </div>
          ) : null}
          <div className="ml-4 mt-2 text-p-md">{user.fullName}</div>
          <div className="ml-4 text-p-sm text-secondary">{user.email}</div>
          <div className="ml-4 text-p-xs text-tertiary">Role: {user.role}</div>
          <Logout activeApp={activeApp} />
        </Dropdown>
      )}
    </>
  )
}

const UserStyled = styled.div<{ activeApp: APP_TYPE; smallScreen: boolean }>`
  padding-top: 2px;
  margin-top: 4px;
  flex-direction: row;
  margin-bottom: 4px;
  padding-bottom: 2px;
  align-items: center;
  display: flex;
  position: relative;

  :hover {
    ${({ activeApp }) => (activeApp === APP_TYPE.SALES ? 'background-color: #E7E6E4;' : 'background-color: #2E2B29;')}
    cursor: pointer;
    font-weight: 700;

    ::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 0.25rem;
      background-color: ${COLORS.green[300]}; // Color of the line
    }
  }
`

const InitialsContainer = styled.div<{ smallScreen: boolean }>`
  background-color: ${COLORS.green[300]};
  border-radius: 20px;
  align-items: center;
  justify-content: end;
  margin-left: 0.5rem;
  flex-shrink: 0;

  ${({ smallScreen }) =>
    smallScreen
      ? 'max-width: 2.5rem; max-height: 2.5rem; transform: translateX(-50%);'
      : 'width: 40px; height: 40px; margin-right: 16px;'}
`

const InitialsStyled = styled.div<{ activeApp: APP_TYPE }>`
  font-weight: 500;
  line-height: 40px;
  text-align: center;

  ${({ activeApp }) => (activeApp === APP_TYPE.SALES ? 'color: #000000;' : 'color: white;')}
`
const TitleContainerStyled = styled.div<{ smallScreen: boolean }>`
  flex: 1;
  font-weight: 300;
  margin-right: 16;

  ${({ smallScreen }) => (smallScreen ? 'transform: translateX(-10%);' : '')}
`

const TitleStyled = styled.div<{ activeApp: APP_TYPE; smallScreen: boolean }>`
  margin-bottom: 4;
  font-size: 16;
  overflow: hidden;
  text-overflow: ellipsis;

  font-size: var(--text-p-sm-size, 0.875rem);
  line-height: var(--text-p-sm-line-height, 1.25rem);

  ${({ smallScreen }) => (smallScreen ? 'max-width: 100%;' : 'max-width: 60%;')}

  ${({ activeApp }) => (activeApp === APP_TYPE.SALES ? 'color: #000000;' : 'color: white;')}
`

export const UserChildStyled = styled.div<{ activeApp: APP_TYPE }>`
  margin-top: 6px;
  margin-bottom: 4px;
  padding-left: 2px;
  padding-right: 2px;
  flex-direction: row;
  display: flex;
  position: relative;
`

export const DropdownChildStyled = styled.div<{ activeApp: APP_TYPE }>`
  padding-top: 2px;
  padding-bottom: 2px;
  margin-top: 4px;
  flex-direction: row;
  display: flex;
  align-items: center;

  border-top-width: 2px;
  border-top-style: solid;
  border-color: #e7e6e4;
  width: 100%;
  :hover {
    background-color: ${COLORS.green[100]};
    border-radius: 0.5rem;
    cursor: pointer;
    color: #000000;
  }
`

const styles = {
  dropdownContainer: {
    top: '-400%',
    left: '4px',
    minWidth: '300px',
    minHeight: '90px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    marginLeft: '12px',
    marginRight: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  textContainer: {
    flex: 1,
    fontWeight: '300',
    marginRight: 16,
  },
}
